import { Point, PositionObject, TooltipPositionerPointObject } from "highcharts";
import IResultInhibitor from "../../interfaces/frontend/IResultInhibitor";
import { InhibitorAbstractChart, IBasePoint, IBaseProps } from "./InhibitorAbstractChart";

interface IPoint extends IBasePoint {
    y: number,
    tooltipSeriesType: string,
}

interface IProps extends IBaseProps {
}

export default class InhibitorColumnChart extends InhibitorAbstractChart<IPoint, IProps> {
    createWtPoint = (inhibitor: IResultInhibitor): IPoint => ({
        ...inhibitor,
        inhibitor,
        y: inhibitor.bindingEnergy.wt,
        tooltipSeriesType: 'WT',
    })
    createMutPoint = (inhibitor: IResultInhibitor): IPoint => ({
        ...inhibitor,
        inhibitor,
        y: inhibitor.bindingEnergy.mut,
        tooltipSeriesType: this.props.mutation,
    })

    getOptions = (): Highcharts.Options => (
        {
            chart: {
                type: 'column',
                height: 250,
            },
            colors: [
                "#7cb5ec", "#1b4260",
            ],
            credits: {
                enabled: false,
            },
            legend: {
                enabled: true,
            },
            title: {
                text: this.props.title,
            },
            yAxis: {
                title: {
                    text: 'Binding energy [kcal/mol]'
                },
                reversed: true,
                min: this.props.min !== undefined ? this.props.min : null,
                max: this.props.max !== undefined ? this.props.max : null,
            },
            xAxis: {
                title: {
                    text: 'Inhibitors',
                },
                labels: {
                    enabled: false,
                },
            },
            series: [{
                name: 'Inhibitors for Wild-Type',
                type: 'column',
                data: this.getData(this.createWtPoint),
            }, {
                name: 'Inhibitors for ' + this.props.mutation,
                type: 'column',
                data: this.getData(this.createMutPoint),
            }],
            plotOptions: {
                series: {
                    allowPointSelect: true,
                    point: {
                        events: {
                            select: this.handleSelect,
                            unselect: this.handleUnselect,
                        },
                    },
                    events: {
                        afterAnimate: this.afterAnimate,
                    },
                    states: {
                        select: {
                            color: '#fffb00',
                        },
                    },
                    animation: false,
                },
            },
            tooltip: {
                shared: true,
                headerFormat: "{point.key}<br><table>",
                footerFormat: "</table>",
                pointFormatter: function() {
                    // "this" refers to point object
                    const point: Point & IPoint = this as unknown as Point & IPoint;
                    return `<tr><td><span style="color:${point.color}">●</span> ${point.tooltipSeriesType}:</td><td><b>${point.y}</b> kcal/mol</td></tr>`
                },
                useHTML: true,
                valueDecimals: 2,
                positioner: function (boxWidth: number, boxHeight: number, point: TooltipPositionerPointObject): PositionObject {
                    // We aim to fix only Y position.
                    // - highcharts: tooltip overlays bar on Y axis
                    // - our solution: tooltip is above bar (better)
                    // If possible, keep X position computed by highcharts.
                    // - highcharts: count also with header width (better)
                    // - our solution: does not count with header width, so if header is wider than tooltip body, tooltip is not centered
                    const positionY = Math.max(point.plotY - boxHeight, 0);
                    if ((this as any).getPosition !== undefined) {
                        // use private highcharts method getPosition if possible
                        return {
                            x: ((this as any).getPosition(boxWidth, boxHeight, point) as PositionObject).x,
                            y: positionY,
                        };
                    } else {
                        return {
                            x: Math.min(Math.max(point.plotX, 0), this.chart.plotWidth - boxWidth / 2 - 5),
                            y: positionY,
                        };
                    }
                },
            },
            exporting: {
                buttons: {
                    contextButton: {
                        enabled: false,
                    }
                },
            },
        }
    );
}
